/**
 * Smart Context helpers for Talk to AI:
 * - keyword-matched context expansion
 * - JSON compaction before stringify
 * - claim professionals + attachable HL7 payloads
 */

import { getDocumentAiAttachState } from './ai-file-support';

export type ContextRule = {
    key: string;
    keywords: string[];
    resolve: () => unknown;
};

export type AttachedItem = {
    type: string;
    id: string;
    label: string;
    data: unknown;
};

export type AttachableEntityItem = {
    id: string;
    summary: string;
    data: unknown;
    /** When true, item cannot be attached (e.g. unsupported PDF). */
    disabled?: boolean;
    /** Shown in a tooltip when disabled. */
    disabledReason?: string;
};

export type AttachableEntityGroup = {
    key: string;
    label: string;
    items: AttachableEntityItem[];
};

export type Hl7Like = {
    id?: string;
    created?: string;
    type?: string;
    message?: string;
    providerProfessional?: unknown;
    supportProfessional?: unknown;
    professional?: unknown;
    providerInstitutional?: unknown;
    supportInstitutional?: unknown;
    institutional?: unknown;
};

/** Matches id, facilityId, groupId, providerIds, *_id, *_ids, etc. */
const ID_FIELD_RE = /(^id$|Ids?$|_ids?$)/i;
const MAX_STRING_LEN = 2000;
const TAG_FIELD_NAMES = new Set(['tags', 'historicalTagRecords']);
/** Always drop these keys from AI context (noise / UI-only). */
const DROP_KEYS = new Set(['revisions', 'starred']);
/** Do not truncate these string fields (e.g. document base64 / raw HL7 message in dataJson). */
const PRESERVE_LONG_STRING_KEYS = new Set(['content', 'contentBase64', 'base64', 'message']);

const PROFESSIONAL_JSON_TYPES = new Set([
    'JSON_SUPPORT_PROFESSIONAL',
    'JSON_PROVIDER_PROFESSIONAL',
]);

/**
 * Extract HL7 payload the same way as timeline claim-data / HL7 detail:
 * structured professional/institutional fields first, else `message` (parsed JSON if possible).
 */
export const extractHl7ContextPayload = (hl7: Hl7Like | Record<string, unknown>): unknown => {
    const record = hl7 as Record<string, unknown>;

    if (record.supportProfessional) {
        return record.supportProfessional;
    }
    if (record.providerProfessional) {
        return record.providerProfessional;
    }
    if (record.supportInstitutional) {
        return record.supportInstitutional;
    }
    if (record.providerInstitutional) {
        return record.providerInstitutional;
    }
    if (record.professional) {
        return record.professional;
    }
    if (record.institutional) {
        return record.institutional;
    }

    if (typeof record.message === 'string' && record.message) {
        try {
            return JSON.parse(record.message);
        } catch {
            return record.message;
        }
    }

    return undefined;
};

const isProfessionalJsonHl7 = (hl7: Hl7Like): boolean => {
    const type = hl7.type || '';
    return (
        PROFESSIONAL_JSON_TYPES.has(type) ||
        !!hl7.providerProfessional ||
        !!hl7.supportProfessional ||
        !!hl7.professional
    );
};

/**
 * Prefer latest HL7 with useful payload (professional fields or message).
 */
export const getLatestHl7ForContext = (
    hl7s?: Hl7Like[] | null,
): { hl7: Hl7Like; payload: unknown } | undefined => {
    if (!hl7s?.length) {
        return undefined;
    }

    const sorted = [...hl7s].sort(
        (a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime(),
    );

    const lastRelevant = sorted.find(hl7 => extractHl7ContextPayload(hl7) !== undefined);
    if (!lastRelevant) {
        return undefined;
    }

    return {
        hl7: lastRelevant,
        payload: extractHl7ContextPayload(lastRelevant),
    };
};

/**
 * Prefer providerProfessional, then supportProfessional, then professional —
 * same order as ClaimEditorStore.determineActivePayload.
 */
export const getLatestHl7Professional = (hl7s?: Hl7Like[] | null): unknown => {
    const latest = getLatestHl7ForContext(hl7s);
    if (!latest || !isProfessionalJsonHl7(latest.hl7)) {
        return undefined;
    }
    return latest.payload;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const questionMatchesKeywords = (question: string, keywords: string[]): boolean => {
    const q = question.toLowerCase();
    return keywords.some(kw => new RegExp(`\\b${escapeRegExp(kw.toLowerCase())}\\b`, 'i').test(q));
};

/**
 * Merge base context with keyword-matched extras.
 * Previously matched keys stay included (accumulate across the conversation).
 */
export const matchContext = (
    question: string,
    base: Record<string, unknown>,
    rules: ContextRule[],
    previouslyMatchedKeys: string[] = [],
): { context: Record<string, unknown>; matchedKeys: string[] } => {
    const matched = new Set(previouslyMatchedKeys);

    for (const rule of rules) {
        if (matched.has(rule.key)) {
            continue;
        }
        if (questionMatchesKeywords(question, rule.keywords)) {
            matched.add(rule.key);
        }
    }

    const extras: Record<string, unknown> = {};
    for (const rule of rules) {
        if (!matched.has(rule.key)) {
            continue;
        }
        const value = rule.resolve();
        if (value !== undefined) {
            extras[rule.key] = value;
        }
    }

    return {
        context: { ...base, ...extras },
        matchedKeys: [...matched],
    };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isAmountShape = (value: unknown): value is { amount: string; currency?: string } => {
    if (!isPlainObject(value)) {
        return false;
    }
    if (typeof value.amount !== 'string') {
        return false;
    }
    const keys = Object.keys(value);
    return keys.every(k => k === 'amount' || k === 'currency');
};

const isTagLike = (value: unknown): value is { name?: string } =>
    isPlainObject(value) && typeof value.name === 'string';

const compactAmount = (value: { amount: string; currency?: string }): string =>
    value.currency ? `${value.amount} ${value.currency}` : value.amount;

const truncateString = (value: string): string => {
    if (value.length <= MAX_STRING_LEN) {
        return value;
    }
    return `${value.slice(0, MAX_STRING_LEN)}…(+${value.length - MAX_STRING_LEN} chars)`;
};

const maybeShortenDate = (key: string, value: string): string => {
    if (PRESERVE_LONG_STRING_KEYS.has(key)) {
        return value;
    }
    const looksLikeIso = /^\d{4}-\d{2}-\d{2}T/.test(value);
    const dateishKey = /date|created|updated|dos/i.test(key);
    if (looksLikeIso && dateishKey) {
        return value.slice(0, 10);
    }
    return truncateString(value);
};

/** Keep claim.id only; strip every other id / *Id / *Ids field (strings or arrays). */
const shouldStripIdField = (key: string, parentKey: string): boolean => {
    if (parentKey === 'claim' && key === 'id') {
        return false;
    }
    return ID_FIELD_RE.test(key);
};

const isAddressLike = (value: unknown): value is Record<string, unknown> =>
    isPlainObject(value) && Array.isArray(value.lines);

/**
 * Address objects → "line1, line2, city, state, postal"
 * (drops name / country / type — same pattern everywhere).
 */
const compactAddress = (addr: Record<string, unknown>): string | undefined => {
    const parts: string[] = [];

    if (Array.isArray(addr.lines)) {
        for (const line of addr.lines) {
            if (typeof line === 'string' && line.trim()) {
                parts.push(line.trim());
            }
        }
    }

    for (const field of ['city', 'state', 'postal'] as const) {
        const part = addr[field];
        if (typeof part === 'string' && part.trim()) {
            parts.push(part.trim());
        }
    }

    return parts.length ? parts.join(', ') : undefined;
};

const shrinkNode = (value: unknown, keyHint = ''): unknown => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    if (typeof value === 'string') {
        return maybeShortenDate(keyHint, value);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (isAmountShape(value)) {
        return compactAmount(value);
    }

    if (isAddressLike(value)) {
        return compactAddress(value);
    }

    if (Array.isArray(value)) {
        if (TAG_FIELD_NAMES.has(keyHint) && value.every(isTagLike)) {
            const names = value.map(t => t.name).filter((n): n is string => !!n);
            return names.length ? names : undefined;
        }

        if (value.length > 0 && value.every(isAddressLike)) {
            const addresses = value.map(compactAddress).filter((s): s is string => !!s);
            return addresses.length ? addresses : undefined;
        }

        const next = value.map(item => shrinkNode(item, keyHint)).filter(v => v !== undefined && v !== null);
        return next.length ? next : undefined;
    }

    if (isPlainObject(value)) {
        const next: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value)) {
            if (v === null || v === undefined || v === '') {
                continue;
            }
            if (DROP_KEYS.has(k)) {
                continue;
            }
            if (shouldStripIdField(k, keyHint)) {
                continue;
            }
            const shrunk = shrinkNode(v, k);
            if (shrunk !== undefined && shrunk !== null) {
                next[k] = shrunk;
            }
        }
        return Object.keys(next).length ? next : undefined;
    }

    return value;
};

/**
 * Compact payload for AI: amounts → strings, tags → name lists, addresses →
 * single-line strings, strip id/*Id/*Ids + revisions/starred/nulls/empties,
 * keep claim.id, shorten dates/long strings.
 */
export const shrinkForAi = (value: unknown): unknown => {
    const shrunk = shrinkNode(value, '');
    return shrunk === undefined ? {} : shrunk;
};

export const stringifyContextForAi = (value: unknown): string | undefined => {
    try {
        return JSON.stringify(shrinkForAi(value));
    } catch {
        return undefined;
    }
};

const formatDateLabel = (value?: string): string => (value ? value.slice(0, 10) : 'unknown date');

const authorNickname = (author: unknown): string | undefined => {
    if (!isPlainObject(author) || typeof author.nickname !== 'string' || !author.nickname) {
        return undefined;
    }
    return author.nickname;
};

export const summarizeMessageItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const author =
        authorNickname(item.author) ?? authorNickname(item.lastAuthor) ?? 'unknown';
    const subject = typeof item.subject === 'string' ? item.subject : '';
    const snippet = subject ? subject.slice(0, 60) : '(no subject)';
    return `${date} · ${author} · ${snippet}`;
};

export const summarizePaymentItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const amountObj = item.payerAmount ?? item.collectedAmount ?? item.amount ?? item.chargeAmount;
    const amount = isAmountShape(amountObj)
        ? compactAmount(amountObj)
        : typeof amountObj === 'string'
        ? amountObj
        : 'n/a';
    const status = typeof item.status === 'string' ? item.status : typeof item.source === 'string' ? item.source : '';
    return [date, amount, status].filter(Boolean).join(' · ');
};

export const summarizeCollectionPaymentItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const amountObj = item.collectedAmount ?? item.amount;
    const amount = isAmountShape(amountObj)
        ? compactAmount(amountObj)
        : typeof amountObj === 'string'
        ? amountObj
        : 'n/a';
    const source = typeof item.source === 'string' ? item.source : '';
    return [date, amount, source].filter(Boolean).join(' · ') || date;
};

export const summarizeTransmissionItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const type = typeof item.type === 'string' ? item.type : '';
    const payerName =
        isPlainObject(item.clearinghousePayer) && typeof item.clearinghousePayer.name === 'string'
            ? item.clearinghousePayer.name
            : '';
    const statuses = Array.isArray(item.statuses) ? item.statuses : [];
    const latestStatus = statuses.length
        ? statuses[statuses.length - 1]
        : undefined;
    const statusLabel =
        isPlainObject(latestStatus) && typeof latestStatus.status === 'string'
            ? latestStatus.status
            : typeof item.status === 'string'
            ? item.status
            : '';
    return [date, type || 'Transmission', payerName, statusLabel].filter(Boolean).join(' · ');
};

export const summarizeInvoiceItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const amountObj = item.amount ?? item.paidAmount;
    const amount = isAmountShape(amountObj)
        ? compactAmount(amountObj)
        : typeof amountObj === 'string'
        ? amountObj
        : 'n/a';
    const invoiceType = typeof item.invoiceType === 'string' ? item.invoiceType : '';
    const status = typeof item.invoiceStatus === 'string' ? item.invoiceStatus : '';
    return [date, invoiceType, amount, status].filter(Boolean).join(' · ') || date;
};

export const summarizeDocumentItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const name = typeof item.filename === 'string' ? item.filename : typeof item.name === 'string' ? item.name : 'document';
    return `${date} · ${name}`;
};

export const summarizeLetterItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(
        typeof item.created === 'string'
            ? item.created
            : typeof item.sent === 'string'
              ? item.sent
              : undefined,
    );
    const nested =
        item.document && typeof item.document === 'object'
            ? (item.document as Record<string, unknown>)
            : null;
    const name =
        (typeof nested?.filename === 'string' && nested.filename) ||
        (typeof item.filename === 'string' && item.filename) ||
        'Letter';
    return `${date} · ${name}`;
};

export const summarizeFaxItem = (item: Record<string, unknown>, direction: 'Received' | 'Sent'): string => {
    const date = formatDateLabel(
        typeof item.received === 'string'
            ? item.received
            : typeof item.created === 'string'
              ? item.created
              : typeof item.sent === 'string'
                ? item.sent
                : undefined,
    );
    const nested =
        item.document && typeof item.document === 'object'
            ? (item.document as Record<string, unknown>)
            : null;
    const name =
        (typeof nested?.filename === 'string' && nested.filename) ||
        (typeof item.filename === 'string' && item.filename) ||
        'Fax';
    const pages = typeof item.pages === 'number' ? `${item.pages}p` : '';
    return [date, direction, name, pages].filter(Boolean).join(' · ');
};

const documentAttachExtras = (item: Record<string, unknown>): Partial<AttachableEntityItem> => {
    const nested =
        item.document && typeof item.document === 'object'
            ? (item.document as Record<string, unknown>)
            : null;
    const hasLinkedFile =
        !('documentId' in item) || (typeof item.documentId === 'string' && !!item.documentId);
    const attachState = getDocumentAiAttachState({
        mimetype:
            (typeof nested?.mimetype === 'string' && nested.mimetype) ||
            (typeof item.mimetype === 'string' ? item.mimetype : undefined),
        filename:
            (typeof nested?.filename === 'string' && nested.filename) ||
            (typeof item.filename === 'string' ? item.filename : undefined),
        name: typeof item.name === 'string' ? item.name : undefined,
        filesize:
            (typeof nested?.filesize === 'number' && nested.filesize) ||
            (typeof item.filesize === 'number' ? item.filesize : undefined),
        hasFile: hasLinkedFile,
    });
    return {
        disabled: attachState.disabled,
        disabledReason: attachState.disabledReason,
    };
};

export const summarizeCodingItem = (item: Record<string, unknown>): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const status = typeof item.status === 'string' ? item.status : '';
    const vendor = typeof item.vendor === 'string' ? item.vendor : '';
    const requestType = typeof item.requestType === 'string' ? item.requestType : '';
    return [date, status, vendor || requestType].filter(Boolean).join(' · ') || date;
};

const createdTime = (item: Record<string, unknown>): number =>
    item.created && typeof item.created === 'string' ? new Date(item.created).getTime() : 0;

const sortByCreatedDesc = (items: Array<Record<string, unknown> & { id: string }>) =>
    [...items].sort((a, b) => createdTime(b) - createdTime(a));

/** Same claim-data detection as timeline (`isClaimDataHl7Message`). */
const isClaimDataHl7Type = (type?: string): boolean => {
    if (!type) {
        return false;
    }
    return (
        type.startsWith('JSON_PROVIDER_') ||
        type.startsWith('JSON_SUPPORT_') ||
        type === 'JSON_PROVIDER_PROFESSIONAL' ||
        type === 'JSON_PROVIDER_INSTITUTIONAL' ||
        type === 'JSON_SUPPORT_PROFESSIONAL' ||
        type === 'JSON_SUPPORT_INSTITUTIONAL'
    );
};

/**
 * Chronological Claim Data index (oldest = 1), matching claim timeline numbering.
 */
const buildClaimDataIndexById = (hl7Items: Array<Record<string, unknown> & { id: string }>): Map<string, number> => {
    const claimDataAsc = [...hl7Items]
        .filter(item => isClaimDataHl7Type(typeof item.type === 'string' ? item.type : undefined))
        .sort((a, b) => createdTime(a) - createdTime(b));

    return new Map(claimDataAsc.map((item, index) => [item.id, index + 1]));
};

export const summarizeHl7Item = (
    item: Record<string, unknown>,
    claimDataIndexById?: Map<string, number>,
): string => {
    const date = formatDateLabel(typeof item.created === 'string' ? item.created : undefined);
    const type = typeof item.type === 'string' ? item.type : undefined;
    const id = typeof item.id === 'string' ? item.id : undefined;

    if (isClaimDataHl7Type(type) && id && claimDataIndexById?.has(id)) {
        return `${date} · Claim Data (${claimDataIndexById.get(id)})`;
    }

    if (isClaimDataHl7Type(type)) {
        return `${date} · Claim Data`;
    }

    return `${date} · HL7`;
};

export type ClaimContextSources = {
    claim: unknown;
    /** Professional claim forms (typically only the latest/active one). */
    professionals?: unknown[];
    hl7s?: Hl7Like[];
    eligibilities?: unknown[];
    codings?: unknown[];
    messages?: unknown[];
    payments?: unknown[];
    collectionPayments?: unknown[];
    transmissions?: unknown[];
    invoices?: unknown[];
    documents?: unknown[];
    letters?: unknown[];
    receiveFaxes?: unknown[];
    sendFaxes?: unknown[];
    /** Claim workflow history from GET .../worker/history (newest last 5 used in base context). */
    history?: unknown[];
};

const toRecord = (item: unknown): Record<string, unknown> | null =>
    isPlainObject(item) ? item : null;

const withId = (item: Record<string, unknown>): item is Record<string, unknown> & { id: string } =>
    typeof item.id === 'string' && !!item.id;

const HISTORY_CONTEXT_LIMIT = 5;

const historyActionTime = (item: unknown): number => {
    if (!isPlainObject(item) || typeof item.actionDate !== 'string') {
        return 0;
    }
    const time = new Date(item.actionDate).getTime();
    return Number.isFinite(time) ? time : 0;
};

/** Newest-first slice of workflow history for AI base context. */
export const takeLatestHistoryEvents = (items: unknown[] | undefined, limit = HISTORY_CONTEXT_LIMIT): unknown[] => {
    if (!items?.length) {
        return [];
    }
    return [...items].sort((a, b) => historyActionTime(b) - historyActionTime(a)).slice(0, limit);
};

/**
 * Shared claim keyword rules (eligibility / coding / messages+tasks).
 * Professional claim form data comes from `sources.professionals` in the base context.
 */
export const buildClaimContextRules = (sources: ClaimContextSources): ContextRule[] => [
    {
        key: 'eligibilities',
        keywords: ['eligibility', 'eligible', 'coverage', 'insurance'],
        resolve: () => sources.eligibilities,
    },
    {
        key: 'codings',
        keywords: ['coding', 'codings', 'cpt', 'icd'],
        resolve: () => sources.codings,
    },
    {
        key: 'messages',
        keywords: ['message', 'messages', 'task', 'tasks', 'thread', 'follow-up', 'followup'],
        resolve: () => sources.messages,
    },
];

export const buildClaimAttachableEntities = (sources: ClaimContextSources): AttachableEntityGroup[] => {
    const mapGroup = (
        key: string,
        label: string,
        items: unknown[] | undefined,
        summarize: (item: Record<string, unknown>) => string,
        sortDescByCreated = false,
        mapItem?: (item: Record<string, unknown> & { id: string }) => Partial<AttachableEntityItem>,
    ): AttachableEntityGroup => {
        let mapped = (items || [])
            .map(toRecord)
            .filter((item): item is Record<string, unknown> & { id: string } => !!item && withId(item));

        if (sortDescByCreated) {
            mapped = sortByCreatedDesc(mapped);
        }

        return {
            key,
            label,
            items: mapped.map(item => ({
                id: item.id,
                summary: summarize(item),
                data: item,
                ...(mapItem?.(item) || {}),
            })),
        };
    };

    const hl7Records = (sources.hl7s || [])
        .map(toRecord)
        .filter((item): item is Record<string, unknown> & { id: string } => !!item && withId(item));
    const claimDataIndexById = buildClaimDataIndexById(hl7Records);

    return [
        mapGroup('messages', 'Messages', sources.messages, summarizeMessageItem),
        mapGroup('payments', 'Payments', sources.payments, summarizePaymentItem, true),
        mapGroup(
            'collectionPayments',
            'Collection payments',
            sources.collectionPayments,
            summarizeCollectionPaymentItem,
            true,
        ),
        mapGroup('transmissions', 'Transmissions', sources.transmissions, summarizeTransmissionItem, true),
        mapGroup('invoices', 'Invoices', sources.invoices, summarizeInvoiceItem, true),
        mapGroup('documents', 'Documents', sources.documents, summarizeDocumentItem, true, documentAttachExtras),
        mapGroup('letters', 'Letters', sources.letters, summarizeLetterItem, true, documentAttachExtras),
        mapGroup(
            'faxes',
            'Faxes',
            [...(sources.receiveFaxes || []), ...(sources.sendFaxes || [])],
            item => {
                const isReceived =
                    typeof item.received === 'string' ||
                    (sources.receiveFaxes || []).some(
                        fax => isPlainObject(fax) && fax.id === item.id,
                    );
                return summarizeFaxItem(item, isReceived ? 'Received' : 'Sent');
            },
            true,
            documentAttachExtras,
        ),
        mapGroup('codings', 'Codings', sources.codings, summarizeCodingItem, true),
        mapGroup(
            'hl7s',
            'HL7',
            sources.hl7s as unknown[] | undefined,
            item => summarizeHl7Item(item, claimDataIndexById),
            true,
        ),
    ].filter(group => group.items.length > 0);
};

/** Attach picker groups for messaging Talk to AI (comment/document attachments on the thread). */
export const buildMessageAttachableEntities = (
    documents: unknown[] | undefined,
): AttachableEntityGroup[] => {
    let mapped = (documents || [])
        .map(toRecord)
        .filter((item): item is Record<string, unknown> & { id: string } => !!item && withId(item));

    mapped = sortByCreatedDesc(mapped);

    const group: AttachableEntityGroup = {
        key: 'documents',
        label: 'Attachments',
        items: mapped.map(item => ({
            id: item.id,
            summary: summarizeDocumentItem(item),
            data: item,
            ...documentAttachExtras(item),
        })),
    };

    return group.items.length > 0 ? [group] : [];
};

export const buildClaimSmartContext = (args: {
    question: string;
    sources: ClaimContextSources;
    attachedItems: AttachedItem[];
    previouslyMatchedKeys?: string[];
}): { context: unknown; matchedKeys: string[] } => {
    const base: Record<string, unknown> = {
        claim: args.sources.claim,
    };

    const recentHistory = takeLatestHistoryEvents(args.sources.history);
    if (recentHistory.length) {
        base.history = recentHistory;
    }

    if (args.sources.professionals?.length) {
        base.professionals = args.sources.professionals;
    }

    const { context, matchedKeys } = matchContext(
        args.question,
        base,
        buildClaimContextRules(args.sources),
        args.previouslyMatchedKeys,
    );

    if (args.attachedItems.length) {
        const byType: Record<string, unknown[]> = {};
        for (const item of args.attachedItems) {
            // File-bearing attaches are sent via context.file; skip embedding bytes in dataJson.
            if (item.type === 'documents' || item.type === 'letters' || item.type === 'faxes') {
                continue;
            }
            if (!byType[item.type]) {
                byType[item.type] = [];
            }

            if (item.type === 'hl7s' && isPlainObject(item.data)) {
                const hl7 = item.data as Hl7Like;
                const payload = extractHl7ContextPayload(hl7);
                if (isProfessionalJsonHl7(hl7)) {
                    byType[item.type].push({
                        id: hl7.id,
                        type: hl7.type,
                        created: hl7.created,
                        professional: payload,
                    });
                } else {
                    byType[item.type].push({
                        id: hl7.id,
                        type: hl7.type,
                        created: hl7.created,
                        message: payload,
                    });
                }
            } else {
                byType[item.type].push(item.data);
            }
        }
        if (Object.keys(byType).length) {
            context.attached = byType;
        }
    }

    return {
        context: shrinkForAi(context),
        matchedKeys,
    };
};
